import { supabase } from '../config/supabase';

// Generate a unique room name
const generateRoomName = () => {
  const adjectives = ['Swift', 'Bright', 'Cosmic', 'Digital', 'Electric', 'Future', 'Golden', 'Hidden', 'Infinite', 'Mystic'];
  const nouns = ['Cloud', 'Bridge', 'Portal', 'Space', 'Zone', 'Hub', 'Lab', 'Studio', 'Vault', 'Chamber'];
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const number = Math.floor(Math.random() * 9999);
  return `${adjective}${noun}${number}`;
};

// Generate a secure 10-digit password
const generatePassword = () => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = '!@#$%&*';
  
  let password = '';
  
  // Ensure at least one character from each category
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];
  
  // Fill remaining 6 characters
  const allChars = uppercase + lowercase + numbers + special;
  for (let i = 4; i < 10; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

// Get client IP address
const getClientIp = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error fetching IP address:', error);
    return null;
  }
};

export const roomService = {
  // Create a new room
  async createRoom() {
    try {
      const ipAddress = await getClientIp();

      if (ipAddress) {
        try {
          const { data: canCreate, error: checkError } = await supabase
            .rpc('can_ip_create_room', { ip_address: ipAddress });

          if (!checkError && !canCreate) {
            return {
              success: false,
              error: 'You can only create one room at a time. Please wait until your existing room expires (1 hour).'
            };
          }
        } catch (e) {
          console.warn('Rate limiting check failed:', e);
        }
      }

      const roomName = generateRoomName();
      const password = generatePassword();

      const { data, error } = await supabase
        .from('rooms')
        .insert([
          {
            room_name: roomName,
            password: password,
            created_at: new Date().toISOString(),
            last_activity: new Date().toISOString(),
            is_expired: false,
            creator_ip: ipAddress
          }
        ])
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        room: {
          name: data.room_name,
          password: data.password,
          expiresAt: data.expires_at,
          createdAt: data.created_at
        }
      };
    } catch (error) {
      console.error('Error creating room:', error);
      return {
        success: false,
        error: error.message || 'Failed to create room'
      };
    }
  },

  // Verify room exists and password is correct
  async verifyRoom(roomName, password) {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .eq('room_name', roomName)
        .eq('password', password)
        .single();

      if (error && error.code === 'PGRST116') {
        return { success: false, error: 'Invalid room name or password' };
      }
      if (error) throw error;

      const now = new Date();
      const createdAt = new Date(data.created_at);
      const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);

      if (hoursSinceCreation >= 1) {
        return { success: false, error: 'This room has expired' };
      }

      await supabase
        .from('rooms')
        .update({ last_activity: new Date().toISOString() })
        .eq('room_name', roomName);

      return { success: true, room: data };
    } catch (error) {
      console.error('Error verifying room:', error);
      return { success: false, error: error.message || 'Failed to verify room' };
    }
  },

  // Get total room count (Cumulative)
  async getTotalRoomCount() {
    try {
      // Query the permanent statistics table
      const { data, error } = await supabase
        .from('site_statistics')
        .select('total_rooms_created')
        .eq('id', 'global')
        .single();

      if (error) {
        // Fallback to RPC if direct table access fails
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_total_room_count');
        if (rpcError) throw rpcError;
        return { success: true, count: rpcData || 1349 };
      }

      return { 
        success: true, 
        count: parseInt(data.total_rooms_created) || 1349 
      };
    } catch (error) {
      console.error('Error getting total room count:', error);
      return { success: false, count: 1349, error: error.message };
    }
  },

  // Get active room count (Currently in DB)
  async getActiveRoomCount() {
    try {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { count, error } = await supabase
        .from('rooms')
        .select('*', { count: 'exact', head: true })
        .gt('created_at', oneHourAgo);

      if (error) throw error;
      return { success: true, count: count || 0 };
    } catch (error) {
      console.error('Error getting active room count:', error);
      return { success: false, count: 0 };
    }
  },

  async updateRoomActivity(roomName) {
    await supabase.from('rooms').update({ last_activity: new Date().toISOString() }).eq('room_name', roomName);
  },

  async getRoomExpiration(roomName) {
    const { data } = await supabase.from('rooms').select('created_at').eq('room_name', roomName).single();
    if (!data) return { success: false, isExpired: true };
    const now = new Date();
    const createdAt = new Date(data.created_at);
    const minutesSince = (now - createdAt) / (1000 * 60);
    return { success: true, timeRemaining: Math.max(0, 60 - Math.floor(minutesSince)), isExpired: minutesSince >= 60 };
  },

  async checkRoomExists(roomName) {
    const { data } = await supabase.from('rooms').select('created_at').eq('room_name', roomName).single();
    if (!data) return { exists: false };
    const minutesSince = (new Date() - new Date(data.created_at)) / (1000 * 60);
    return { exists: minutesSince < 60 };
  }
};